<script setup lang="ts">
import axios from 'axios'
import { Loader2, Search } from 'lucide-vue-next'

import WeekSchedule from './WeekSchedule.vue'

interface TimeSlot {
  startHour: string
  endHour: string
}

interface DaySchedule {
  weekday: number
  slots: TimeSlot[]
}

interface StoreSchedule {
  storeId: number
  name: string
  timezone: string
  days: DaySchedule[]
}

interface OpeningHourPayload {
  weekday: number
  startHour: string
  endHour: string
}

interface StoreRef {
  storeId: number
  name: string
}

interface HoursGroup {
  key: string
  days: DaySchedule[]
  summary: string
  stores: StoreRef[]
  matches: StoreRef[]
}

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7]
const PREVIEW_COUNT = 3

const { t } = useI18n()
const notification = useNotification()
const { hasPermissionBySlug } = useAccess()

const emptyWeek = (): DaySchedule[] => WEEKDAYS.map((weekday) => ({ weekday, slots: [] }))

const cloneDays = (days: DaySchedule[]): DaySchedule[] =>
  days.map((day) => ({
    weekday: day.weekday,
    slots: day.slots.map((slot) => ({ ...slot }))
  }))

const toWeek = (hours: OpeningHourPayload[]): DaySchedule[] => {
  const week = emptyWeek()

  hours.forEach((hour) => {
    week
      .find((day) => day.weekday === hour.weekday)
      ?.slots.push({
        startHour: hour.startHour.slice(0, 5),
        endHour: hour.endHour.slice(0, 5)
      })
  })

  return week
}

const signature = (days: DaySchedule[]): string =>
  JSON.stringify(
    days.map((day) => ({
      weekday: day.weekday,
      slots: day.slots.map((slot) => `${slot.startHour.slice(0, 5)}-${slot.endHour.slice(0, 5)}`)
    }))
  )

const formatCompactHour = (value: string): string => {
  const [hour = '0', minute = '00'] = value.slice(0, 5).split(':')
  const compactHour = String(Number(hour))

  return minute === '00' ? `${compactHour}h` : `${compactHour}h${minute}`
}

const slotKey = (day: DaySchedule): string =>
  day.slots.map((slot) => `${slot.startHour.slice(0, 5)}-${slot.endHour.slice(0, 5)}`).join(',')

const formatWeek = (days: DaySchedule[]): string => {
  if (days.every((day) => day.slots.length === 0)) {
    return t('pages.settings.storesPage.openingHours.closedAllWeek')
  }

  const parts: string[] = []
  let index = 0

  while (index < days.length) {
    const key = slotKey(days[index])
    let end = index

    while (end + 1 < days.length && slotKey(days[end + 1]) === key) {
      end += 1
    }

    if (key) {
      const startLabel = t(`pages.settings.storesPage.openingHours.weekdaysShort.${days[index].weekday}`)
      const endLabel = t(`pages.settings.storesPage.openingHours.weekdaysShort.${days[end].weekday}`)
      const range = index === end ? startLabel : `${startLabel}–${endLabel}`
      const hours = days[index].slots
        .map((slot) => `${slot.startHour.slice(0, 5)}–${slot.endHour.slice(0, 5)}`)
        .join(', ')
      parts.push(`${range} ${hours}`)
    }

    index = end + 1
  }

  return parts.join(' · ')
}

const toOpeningDays = (days: DaySchedule[]) =>
  days.map((day) => ({
    weekday: day.weekday,
    openingHours: day.slots
      .filter((slot) => slot.startHour && slot.endHour)
      .map((slot) => ({
        startHour: slot.startHour.slice(0, 5),
        endHour: slot.endHour.slice(0, 5)
      }))
  }))

const hasIncompleteSlot = (days: DaySchedule[]): boolean =>
  days.some((day) => day.slots.some((slot) => !slot.startHour || !slot.endHour))

const hasInvalidTimeRange = (days: DaySchedule[]): boolean =>
  days.some((day) => day.slots.some((slot) => slot.startHour && slot.endHour && slot.endHour <= slot.startHour))

const previewNames = (stores: StoreRef[]): string => {
  const shown = stores
    .slice(0, PREVIEW_COUNT)
    .map((store) => store.name)
    .join(', ')
  const remaining = stores.length - PREVIEW_COUNT

  if (remaining <= 0) {
    return shown
  }

  return `${shown} ${t('pages.settings.storesPage.openingHours.andMore', { count: remaining })}`
}

const showsStoreList = (group: HoursGroup): boolean =>
  search.value.trim().length > 0 || group.stores.length <= PREVIEW_COUNT

const listedStores = (group: HoursGroup): StoreRef[] => (search.value.trim() ? group.matches : group.stores)

const editor = ref<HTMLElement | null>(null)
const isLoading = ref(true)
const isSaving = ref(false)
const target = ref<'all' | 'selection'>('all')
const selectedStoreIds = ref<number[]>([])
const search = ref('')
const sharedDays = ref<DaySchedule[]>(emptyWeek())
const storeSchedules = ref<StoreSchedule[]>([])

const canUpdate = computed(() => hasPermissionBySlug('global-configuration.update'))
const timezoneLabel = computed(() =>
  [...new Set(storeSchedules.value.map((store) => store.timezone))].sort().join(', ')
)
const targetCount = computed(() =>
  target.value === 'all' ? storeSchedules.value.length : selectedStoreIds.value.length
)
const hoursDiffer = computed(() => groups.value.length > 1)

const schedulePreview = computed(() =>
  sharedDays.value.map((day) => {
    const label = `${t(`pages.settings.storesPage.openingHours.weekdaysShort.${day.weekday}`)}.`
    const hours =
      day.slots.length === 0
        ? t('pages.settings.storesPage.openingHours.closed')
        : day.slots.map((slot) => `${formatCompactHour(slot.startHour)}-${formatCompactHour(slot.endHour)}`).join(' / ')

    return { weekday: day.weekday, line: `${label} ${hours}` }
  })
)

const groups = computed<HoursGroup[]>(() => {
  const grouped = new Map<string, HoursGroup>()

  storeSchedules.value.forEach((store) => {
    const key = signature(store.days)
    const existing = grouped.get(key)

    if (existing) {
      existing.stores.push({ storeId: store.storeId, name: store.name })
      return
    }

    grouped.set(key, {
      key,
      days: store.days,
      summary: formatWeek(store.days),
      stores: [{ storeId: store.storeId, name: store.name }],
      matches: []
    })
  })

  return [...grouped.values()]
    .map((group) => ({
      ...group,
      stores: [...group.stores].sort((a, b) => a.name.localeCompare(b.name))
    }))
    .sort((a, b) => b.stores.length - a.stores.length || a.summary.localeCompare(b.summary))
})

const visibleGroups = computed(() => {
  const query = search.value.trim().toLowerCase()

  return groups.value
    .map((group) => ({
      ...group,
      matches: query ? group.stores.filter((store) => store.name.toLowerCase().includes(query)) : group.stores
    }))
    .filter((group) => group.matches.length > 0)
})

const loadSchedule = (days: DaySchedule[], storeIds: number[]) => {
  sharedDays.value = cloneDays(days)

  if (storeIds.length === storeSchedules.value.length) {
    target.value = 'all'
    selectedStoreIds.value = []
  } else {
    target.value = 'selection'
    selectedStoreIds.value = [...storeIds]
  }

  editor.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const fetchOpeningHours = async () => {
  isLoading.value = true

  try {
    const { data } = await axios.get<{
      data: { storeId: number; name: string; timezone: string; openingHours: OpeningHourPayload[] }[]
    }>('/opening-hours')

    storeSchedules.value = data.data.map((store) => ({
      storeId: store.storeId,
      name: store.name,
      timezone: store.timezone,
      days: toWeek(store.openingHours)
    }))

    const mainGroup = [...groups.value].sort((a, b) => b.stores.length - a.stores.length)[0]
    sharedDays.value = cloneDays(mainGroup?.days ?? emptyWeek())
    target.value = 'all'
    selectedStoreIds.value = []
  } catch {
    notification.create('error', {
      text: t('pages.settings.storesPage.openingHours.notifications.error')
    })
  } finally {
    isLoading.value = false
  }
}

const save = async () => {
  if (!canUpdate.value || isSaving.value) {
    return
  }

  if (target.value === 'selection' && selectedStoreIds.value.length === 0) {
    notification.create('error', {
      text: t('pages.settings.storesPage.openingHours.selectStores')
    })
    return
  }

  if (hasIncompleteSlot(sharedDays.value)) {
    notification.create('error', {
      text: t('pages.settings.storesPage.openingHours.notifications.invalid')
    })
    return
  }

  if (hasInvalidTimeRange(sharedDays.value)) {
    notification.create('error', {
      text: t('pages.settings.storesPage.openingHours.notifications.invalidRange')
    })
    return
  }

  const openingDays = toOpeningDays(sharedDays.value)
  const payload =
    target.value === 'all'
      ? { applyToAll: true, openingDays }
      : {
          applyToAll: false,
          stores: selectedStoreIds.value.map((storeId) => ({ storeId, openingDays }))
        }

  isSaving.value = true

  try {
    const { data } = await axios.put<{
      data: { storeId: number; name: string; timezone: string; openingHours: OpeningHourPayload[] }[]
    }>('/opening-hours/bulk', payload)

    storeSchedules.value = data.data.map((store) => ({
      storeId: store.storeId,
      name: store.name,
      timezone: store.timezone,
      days: toWeek(store.openingHours)
    }))

    notification.create('success', {
      text: t('pages.settings.storesPage.openingHours.notifications.success')
    })
  } catch {
    notification.create('error', {
      text: t('pages.settings.storesPage.openingHours.notifications.error')
    })
  } finally {
    isSaving.value = false
  }
}

onMounted(fetchOpeningHours)
</script>

<template>
  <div v-if="isLoading" class="mt-6 h-48 animate-pulse rounded-xl border bg-muted" />

  <div v-else-if="storeSchedules.length > 0" class="mt-6 space-y-6 pb-12">
    <form ref="editor" class="border bg-white p-6 sm:rounded-lg" @submit.prevent="save">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 class="text-lg font-medium text-foreground">
            {{ t('pages.settings.storesPage.openingHours.title') }}
          </h2>
          <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
            {{ t('pages.settings.storesPage.openingHours.description') }}
          </p>
          <p v-if="timezoneLabel" class="mt-1 text-sm text-muted-foreground">
            {{ t('pages.settings.storesPage.openingHours.timezone', { timezone: timezoneLabel }) }}
          </p>
        </div>

        <div class="flex shrink-0 flex-col gap-3 sm:items-end">
          <Button v-if="canUpdate" type="submit" :disabled="isSaving || targetCount === 0">
            <Loader2 v-if="isSaving" class="animate-spin" />
            {{ t('pages.settings.storesPage.openingHours.saveCount', { count: targetCount }) }}
          </Button>

          <ul class="space-y-0.5 text-sm text-muted-foreground sm:text-right" aria-live="polite">
            <li v-for="day in schedulePreview" :key="day.weekday">{{ day.line }}</li>
          </ul>
        </div>
      </div>

      <div v-if="storeSchedules.length > 1" class="mt-6 space-y-4">
        <div class="inline-flex rounded-control border bg-muted p-1">
          <button
            type="button"
            class="cursor-pointer rounded-control px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed"
            :class="target === 'all' ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground'"
            :disabled="!canUpdate"
            @click="target = 'all'"
          >
            {{ t('pages.settings.storesPage.openingHours.targetAll', { count: storeSchedules.length }) }}
          </button>
          <button
            type="button"
            class="cursor-pointer rounded-control px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed"
            :class="target === 'selection' ? 'bg-background text-foreground shadow-xs' : 'text-muted-foreground'"
            :disabled="!canUpdate"
            @click="target = 'selection'"
          >
            {{ t('pages.settings.storesPage.openingHours.targetSelection') }}
          </button>
        </div>

        <MultiSelectStores
          v-if="target === 'selection'"
          v-model="selectedStoreIds"
          :has-label="false"
          :disabled="!canUpdate"
          class="sm:w-full sm:max-w-xl"
        />

        <p v-if="target === 'all' && hoursDiffer" class="text-sm text-muted-foreground">
          {{ t('pages.settings.storesPage.openingHours.differentHours') }}
        </p>
        <p v-else-if="target === 'selection'" class="text-sm text-muted-foreground">
          {{ t('pages.settings.storesPage.openingHours.selectionHint') }}
        </p>
      </div>

      <WeekSchedule v-model:days="sharedDays" class="mt-2" :disabled="!canUpdate" />
    </form>

    <section v-if="storeSchedules.length > 1" class="border bg-white p-6 sm:rounded-lg">
      <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 class="text-lg font-medium text-foreground">
            {{ t('pages.settings.storesPage.openingHours.groupsTitle') }}
          </h2>
          <p class="mt-1 text-sm text-muted-foreground">
            {{ t('pages.settings.storesPage.openingHours.groupsDescription') }}
          </p>
        </div>

        <div class="relative w-full sm:max-w-xs">
          <Search class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            v-model="search"
            type="search"
            class="pl-9"
            :placeholder="t('pages.settings.storesPage.openingHours.search')"
          />
        </div>
      </div>

      <p v-if="visibleGroups.length === 0" class="mt-6 text-sm text-muted-foreground">
        {{ t('pages.settings.storesPage.openingHours.noResult') }}
      </p>

      <div v-else class="mt-4 divide-y divide-border">
        <article v-for="group in visibleGroups" :key="group.key" class="py-4 first:pt-0 last:pb-0">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div class="min-w-0">
              <p class="text-sm font-medium text-foreground">{{ group.summary }}</p>
              <p class="mt-1 text-sm text-muted-foreground">
                {{ t('pages.settings.storesPage.openingHours.storeCount', { count: group.stores.length }) }}
                <template v-if="!showsStoreList(group)"> — {{ previewNames(group.stores) }}</template>
              </p>
            </div>

            <Button
              v-if="canUpdate"
              type="button"
              variant="outline"
              size="sm"
              @click="
                loadSchedule(
                  group.days,
                  group.stores.map((store) => store.storeId)
                )
              "
            >
              {{ t('pages.settings.storesPage.openingHours.loadSchedule') }}
            </Button>
          </div>

          <div v-if="showsStoreList(group)" class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="store in listedStores(group)"
              :key="store.storeId"
              type="button"
              class="rounded-control border px-2.5 py-1 text-left text-sm text-foreground hover:bg-muted disabled:cursor-default"
              :disabled="!canUpdate"
              @click="loadSchedule(group.days, [store.storeId])"
            >
              {{ store.name }}
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>
